import { baseApi } from '@shared/services/api/baseApi';
import { tokenStorage } from '@shared/services/storage/tokenStorage';
import { ApiResponse } from '@shared/types/api.types';
import { authLogger } from '@shared/utils/logger';

// ============== Admin Auth Interfaces ==============

export interface AdminLoginRequest {
  email: string;
  password: string;
}

// Backend response structure
export interface AdminLoginData {
  accessToken: string;
  refreshToken: string;
  expiresIn: number; // Backend uses expiresIn, not accessExpiryMillis
}

export interface AdminLoginResponse {
  accessToken: string;
  refreshToken: string;
  accessExpiryMillis: number;
}

export interface AdminRegisterRequest {
  fullName: string;
  email: string;
  mobileNo: string;
  password: string;
  role: string; // 'ADMIN' | 'SUPER_ADMIN'
}

export interface AdminUserResponse {
  userUuid: string;
  fullName: string;
  email: string;
  mobileNo: string;
  role: string;
  status: string;
}

// ============== OTP Auth Interfaces ==============

export interface SendOtpRequest {
  mobile: string;
}

export interface SendOtpResponse {
  message: string;
  otpSentAt: string;
}

export interface VerifyOtpRequest {
  mobile: string;
  otp: string;
}

export interface VerifyOtpResponse {
  accessToken: string;
  refreshToken: string;
  accessExpiryMillis: number;
  isNewUser: boolean;
  requiresRegistration?: boolean;
}

export interface CreateUserRequest {
  name: string;
  email?: string;
  // Add other user fields as needed
}

export interface CreateUserResponse {
  accessToken: string;
  refreshToken: string;
  accessExpiryMillis: number;
  user: User;
}

// ============== Token Management Interfaces ==============

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken?: string;  // Optional - backend may or may not return new refresh token
  expiresIn?: number;     // Backend might use this field
  accessExpiryMillis?: number;  // Or this field
}

// ============== Common Interfaces ==============

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  phone: string;
}

export interface LoginResponse {
  token: string;
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
  };
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  mobile?: string;
  verified?: boolean;
}

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ============== Admin Authentication ==============
    
    /**
     * Admin login with email and password
     * POST /auth/admin/login
     */
    adminLogin: builder.mutation<AdminLoginResponse, AdminLoginRequest>({
      query: (credentials) => ({
        url: '/auth/admin/login',
        method: 'POST',
        body: credentials,
      }),
      transformResponse: (response: ApiResponse<AdminLoginData>) => {
        // Transform backend response to frontend format
        if (response.data) {
          return {
            accessToken: response.data.accessToken,
            refreshToken: response.data.refreshToken,
            accessExpiryMillis: response.data.expiresIn,
          };
        }
        throw new Error('Invalid response format');
      },
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Store tokens after successful login
          tokenStorage.setTokens(
            data.accessToken,
            data.refreshToken,
            data.accessExpiryMillis
          );
        } catch (error) {
          // Error handling
          authLogger.error('Admin login failed:', error);
        }
      },
      invalidatesTags: ['Auth'],
    }),

    /**
     * Register new admin (SUPER_ADMIN only)
     * POST /auth/admin/register
     */
    adminRegister: builder.mutation<AdminUserResponse, AdminRegisterRequest>({
      query: (userData) => ({
        url: '/auth/admin/register',
        method: 'POST',
        body: userData,
      }),
      transformResponse: (response: ApiResponse<AdminUserResponse>) => response.data!,
      invalidatesTags: ['Admin'],
    }),

    /**
     * List all admin users (SUPER_ADMIN only)
     * GET /api/customers?role=ADMIN
     */
    getAdminUsers: builder.query<AdminUserResponse[], void>({
      query: () => ({ url: '/api/customers', method: 'GET', params: { role: 'ADMIN', page: 0, size: 100 } }),
      transformResponse: (response: ApiResponse<{ content: Array<{ customerUuid: string; name: string; email: string; phoneNumber: string; role: string; status: string }> }>) =>
        (response.data?.content ?? []).map((u) => ({
          userUuid: u.customerUuid,
          fullName: u.name,
          email: u.email,
          mobileNo: u.phoneNumber,
          role: u.role,
          status: u.status,
        })),
      providesTags: ['Admin'],
    }),

    /**
     * Delete admin (soft delete)
     * DELETE /auth/admin/{userId}
     */
    deleteAdmin: builder.mutation<void, number>({
      query: (userId) => ({
        url: `/auth/admin/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Admin'],
    }),

    // ============== OTP Authentication ==============
    
    /**
     * Send OTP to mobile number
     * POST /auth/send-otp
     */
    sendOtp: builder.mutation<SendOtpResponse, SendOtpRequest>({
      query: (body) => ({
        url: '/auth/send-otp',
        method: 'POST',
        body,
      }),
    }),

    /**
     * Verify OTP
     * POST /auth/verify-otp
     */
    verifyOtp: builder.mutation<VerifyOtpResponse, VerifyOtpRequest>({
      query: (body) => ({
        url: '/auth/verify-otp',
        method: 'POST',
        body,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Store tokens if user is registered
          if (!data.requiresRegistration) {
            tokenStorage.setTokens(
              data.accessToken,
              data.refreshToken,
              data.accessExpiryMillis
            );
          }
        } catch (error) {
          authLogger.error('OTP verification failed:', error);
        }
      },
      invalidatesTags: ['Auth'],
    }),

    /**
     * Create new user after OTP verification
     * POST /auth/create-user
     */
    createUser: builder.mutation<CreateUserResponse, CreateUserRequest>({
      query: (body) => ({
        url: '/auth/create-user',
        method: 'POST',
        body,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Store tokens after user creation
          tokenStorage.setTokens(
            data.accessToken,
            data.refreshToken,
            data.accessExpiryMillis
          );
        } catch (error) {
          authLogger.error('User creation failed:', error);
        }
      },
      invalidatesTags: ['Auth'],
    }),

    // ============== Token Management ==============
    
    /**
     * Refresh access token using refresh token
     * POST /auth/refresh
     */
    refreshToken: builder.mutation<RefreshTokenResponse, RefreshTokenRequest>({
      query: (body) => ({
        url: '/auth/refresh',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<RefreshTokenResponse>) => {
        // Backend returns data in nested format
        if (response.data) {
          return response.data;
        }
        throw new Error('Invalid refresh response format');
      },
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          
          // Support both field names from backend
          const expiryTime = data.expiresIn || data.accessExpiryMillis;
          
          if (!expiryTime) {
            throw new Error('Missing expiry time in refresh response');
          }
          
          // Update both access and refresh tokens
          // Backend may return new refresh token for security (token rotation)
          if (data.refreshToken) {
            tokenStorage.setTokens(data.accessToken, data.refreshToken, expiryTime);
          } else {
            // Fallback: only update access token if no new refresh token
            tokenStorage.setAccessToken(data.accessToken, expiryTime);
          }
        } catch (error) {
          console.error('Token refresh failed:', error);
          // Clear tokens on refresh failure
          tokenStorage.clearTokens();
        }
      },
    }),

    // ============== Legacy/Common Endpoints ==============

    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['Auth'],
    }),

    register: builder.mutation<LoginResponse, RegisterRequest>({
      query: (userData) => ({
        url: '/auth/register',
        method: 'POST',
        body: userData,
      }),
    }),

    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } finally {
          // Always clear tokens on logout
          tokenStorage.clearTokens();
        }
      },
      invalidatesTags: ['Auth'],
    }),

    getCurrentUser: builder.query<User, void>({
      query: () => '/auth/me',
      providesTags: ['Auth'],
    }),

    verifyOTP: builder.mutation<LoginResponse, { phone: string; otp: string }>({
      query: (body) => ({
        url: '/auth/verify-otp',
        method: 'POST',
        body,
      }),
    }),
  }),
});

// ============== Export Hooks ==============

export const {
  // Admin Auth
  useAdminLoginMutation,
  useAdminRegisterMutation,
  useGetAdminUsersQuery,
  useDeleteAdminMutation,
  
  // OTP Auth
  useSendOtpMutation,
  useVerifyOtpMutation,
  useCreateUserMutation,
  
  // Token Management
  useRefreshTokenMutation,
  
  // Legacy/Common
  useLoginMutation,
  useRegisterMutation,
  useLogoutMutation,
  useGetCurrentUserQuery,
  useVerifyOTPMutation,
} = authApi;
